import { useEffect, useRef, useState } from 'react';
import { ImageOff } from '../../components/icons';
import { api } from '../../api/client';
import { mediaSrc } from '../../api/media';
import { useAuthStore } from '../../store/auth-store';
import { TableSkeleton } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { Symbol } from '../../components/symbol';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useUrlTab } from '../../services/attention';

const PER_PAGE = 15;

interface ProductRow {
  id: string;
  title: string;
  slug: string;
  status: string;
  mrp: number;
  sellingPrice: number;
  createdAt: string;
  images: Array<{ url: string; altText: string | null }>;
  category: { id: string; name: string; slug: string } | null;
  seller: {
    id: string;
    businessName: string;
    ownerName?: string | null;
    phoneE164?: string | null;
    email?: string | null;
    kycStatus?: string | null;
    onboardingStatus?: string | null;
  } | null;
  _count: { variants: number };
}

interface ProductListResponse {
  items: ProductRow[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_TABS = ['PENDING_REVIEW', 'APPROVED', 'ACTIVE', 'REJECTED', 'INACTIVE', 'ALL'] as const;
type StatusTab = (typeof STATUS_TABS)[number];

export function ProductsPage() {
  const canModerate = useAuthStore((s) => s.hasScope('catalog.product.moderate'));
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [tab, setTab] = useUrlTab<StatusTab>(STATUS_TABS, 'PENDING_REVIEW');
  const [q, setQ] = useState('');
  // Server-searched: the list is paged by the API, so a match on page 7 has to
  // be found there (title, slug, seller, phone, category, price, status, KYC).
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { confirm, prompt } = useConfirm();

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get<ProductDetail>(`/admin/products/${id}`);
      setDetail(data);
    } catch (err) {
      setError(extractErr(err));
    } finally {
      setDetailLoading(false);
    }
  };

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: PER_PAGE };
      if (tab !== 'ALL') params.status = tab;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get<ProductListResponse>('/admin/products', { params });
      if (requestId !== latestRequest.current) return;
      setRows(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Back to page 1 whenever the tab or search changes, so a search made on
  // page 4 does not silently show nothing.
  useEffect(() => { setPage(1); }, [tab, debouncedQ]);
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, debouncedQ, page]);

  /** False only when the admin backed out of the confirmation. */
  const moderate = async (id: string, action: 'approve' | 'reject' | 'pause' | 'resume', title?: string): Promise<boolean> => {
    let body: unknown;
    if (action === 'reject') {
      const reason = await prompt({
        title: 'Reject product?',
        description: `${title ? `“${title}” goes` : 'The listing goes'} back to the seller with your reason. It returns to this queue when they fix and resubmit it.`,
        label: 'Reason for rejection',
        placeholder: 'e.g. Photos are blurry; the price does not match the MRP',
        required: true,
        multiline: true,
        confirmLabel: 'Reject product',
        tone: 'danger',
      });
      if (reason === null) return false;
      body = { reason };
    }
    if (action === 'pause') {
      const ok = await confirm({
        title: 'Pause product?',
        description: `${title ? `“${title}”` : 'This product'} is hidden from shoppers straight away and cannot be ordered. You can resume it at any time.`,
        confirmLabel: 'Pause product',
        tone: 'danger',
      });
      if (!ok) return false;
    }
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/admin/products/${id}/${action}`, body);
      await load();
      return true;
    } catch (err) {
      setError(extractErr(err));
      // Still "acted": the caller closes the drawer so the error banner shows.
      return true;
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const searching = debouncedQ.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products"
        description="Review seller listings and pause or resume live products."
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className="fchip"
          >
            {t === 'PENDING_REVIEW' ? 'Moderation queue' : t === 'ALL' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <TableSearch
        value={q}
        onChange={setQ}
        placeholder="Search by product, seller, phone, category, price or status"
        resultCount={!loading && searching ? total : undefined}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <TableSkeleton cols={6} />
      ) : rows.length === 0 && searching ? (
        <EmptyState
          icon="search_off"
          title={`No matches for “${debouncedQ}”`}
          body="Try another word, or clear the search to see every product in this view."
          action={<button type="button" onClick={() => setQ('')} className="btn btn-secondary">Clear search</button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="inventory_2"
          title="No products in this view"
          body={tab === 'PENDING_REVIEW'
            ? 'Products land here when a seller submits a listing for review.'
            : 'Try another status or clear the search.'}
        />
      ) : (
        <div>
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Seller</th>
                    <th>Category</th>
                    <th className="!text-right">Price</th>
                    <th className="!text-center">Variants</th>
                    <th>Status</th>
                    <th className="!text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <ProductThumb
                            url={p.images[0]?.url ?? ''}
                            alt={p.images[0]?.altText ?? ''}
                            className="h-10 w-10 rounded-sm"
                          />
                          <div className="min-w-0">
                            <button type="button" onClick={() => void openDetail(p.id)} className="text-left font-semibold text-ink-900 hover:text-admin-ink hover:underline">{p.title}</button>
                            <div className="sku text-ink-500">{p.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="align-top">{p.seller ? <SellerCell seller={p.seller} /> : <span className="text-ink-500">—</span>}</td>
                      <td className="text-ink-700">{p.category?.name ?? '—'}</td>
                      <td className="whitespace-nowrap text-right">
                        <span className="font-tabular font-semibold">₹{p.sellingPrice.toLocaleString('en-IN')}</span>
                        {p.mrp > p.sellingPrice && <span className="ml-1 font-tabular text-[12.5px] text-ink-500 line-through">₹{p.mrp.toLocaleString('en-IN')}</span>}
                      </td>
                      <td className="text-center font-tabular text-ink-700">{p._count.variants}</td>
                      <td><StatusPill value={p.status} /></td>
                      <td className="whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button type="button" onClick={() => void openDetail(p.id)} className="btn btn-ghost btn-sm">View</button>
                          {canModerate && <RowActions status={p.status} busy={busyId === p.id} onAction={(a) => void moderate(p.id, a, p.title)} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={total}
              perPage={PER_PAGE}
              onPageChange={setPage}
            />
          </div>
          <p className="mt-3 text-[13.5px] text-ink-500">{total} product{total === 1 ? '' : 's'}</p>
        </div>
      )}

      {(detail || detailLoading) && (
        <ProductDetailModal
          detail={detail}
          loading={detailLoading}
          canModerate={canModerate}
          busy={busyId === detail?.id}
          onClose={() => setDetail(null)}
          onAction={async (a) => { if (detail && (await moderate(detail.id, a, detail.title))) setDetail(null); }}
        />
      )}
    </div>
  );
}

interface ProductDetail {
  id: string;
  title: string;
  slug: string;
  status: string;
  description?: string | null;
  mrp?: number;
  sellingPrice?: number;
  attributes?: Record<string, unknown> | null;
  seller: { id: string; businessName: string; kycStatus: string | null } | null;
  category: { id: string; name: string } | null;
  brand: { id: string; name: string } | null;
  images: Array<{ url?: string; fileUrl?: string; altText?: string | null; variantId?: string | null; isPrimary?: boolean; sortOrder?: number }>;
  /** Product videos — same media table as images, split apart by the API. */
  videos?: Array<{ url?: string; variantId?: string | null; sizeBytes?: number | null }>;
  variants: Array<{ id: string; sku: string; size: string | null; sizeUnit?: string | null; color: string | null; mrpOverride: number | null; sellingPriceOverride: number | null; isActive: boolean }>;
}

function ProductDetailModal({
  detail, loading, canModerate, busy, onClose, onAction,
}: {
  detail: ProductDetail | null;
  loading: boolean;
  canModerate: boolean;
  busy: boolean;
  onClose: () => void;
  onAction: (a: 'approve' | 'reject' | 'pause' | 'resume') => void;
}) {
  // variantId → "Colour · Size" caption, so each photo can show which variant
  // it belongs to (like Flipkart's per-colour galleries).
  const variantLabel = new Map(
    (detail?.variants ?? []).map((v) => [
      v.id,
      [v.color, v.size].filter(Boolean).join(' · ') || v.sku,
    ]),
  );
  const occasions =
    detail?.attributes && Array.isArray((detail.attributes as Record<string, unknown>).occasions)
      ? ((detail.attributes as Record<string, unknown>).occasions as unknown[]).map(String)
      : [];
  // The seller's own category label when nothing in the curated tree fitted.
  // Worth promoting into a real category if the same one keeps appearing.
  const customCategory =
    typeof (detail?.attributes as Record<string, unknown> | undefined)?.customCategory === 'string'
      ? ((detail!.attributes as Record<string, unknown>).customCategory as string)
      : null;
  const videos = detail?.videos ?? [];

  // Full-screen preview of a clicked photo. Escape / backdrop click closes it.
  const [lightbox, setLightbox] = useState<string | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim">
      <div className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <h2 className="min-w-0 font-display text-drawer-title text-ink-900">{loading ? 'Loading…' : detail?.title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="btn btn-quiet btn-icon btn-sm -mr-2 shrink-0">
            <Symbol name="close" size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {detail && (
          <>
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <StatusPill value={detail.status} />
              {detail.seller && <span className="text-[13.5px] text-ink-500">{detail.seller.businessName} · KYC {detail.seller.kycStatus ? sentence(detail.seller.kycStatus) : '—'}</span>}
            </div>

            {detail.images.length > 0 && (
              <div className="mb-5">
                <h3 className="mb-2 text-[14.5px] font-semibold text-ink-900">Photos ({detail.images.length})</h3>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {detail.images.map((img, i) => {
                    const label = img.variantId ? variantLabel.get(img.variantId) ?? '' : '';
                    const src = img.url ?? img.fileUrl ?? '';
                    return (
                      <div key={i} className="shrink-0">
                        <button
                          type="button"
                          onClick={() => src && setLightbox(src)}
                          disabled={!src}
                          title="Click to view full screen"
                          className="relative block cursor-zoom-in rounded-md focus:outline-none focus-visible:shadow-focus disabled:cursor-default"
                        >
                          <ProductThumb
                            url={src}
                            alt={img.altText ?? ''}
                            className="h-28 w-28 rounded-md border border-surface-line"
                          />
                          {img.isPrimary && (
                            <span className="badge badge-solid absolute left-1.5 top-1.5 !h-5 !px-2 !text-[11px]">
                              Primary
                            </span>
                          )}
                        </button>
                        {label && (
                          <p className="mt-1 w-28 truncate text-center text-[12.5px] text-ink-500">{label}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {videos.length > 0 && (
              <div className="mb-5">
                <h3 className="mb-2 text-[14.5px] font-semibold text-ink-900">Videos ({videos.length})</h3>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {videos.map((v, i) => (
                    <div key={i} className="shrink-0">
                      <video
                        src={mediaSrc(v.url)}
                        controls
                        preload="metadata"
                        className="h-28 w-28 rounded-md bg-ink-900 object-cover"
                      />
                      {v.variantId && (
                        <p className="mt-1 w-28 truncate text-center text-[12.5px] text-ink-500">
                          {variantLabel.get(v.variantId) ?? ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.description && <p className="mb-5 text-[14.5px] leading-relaxed text-ink-700">{detail.description}</p>}

            <dl className="card mb-5 grid grid-cols-2 gap-x-4 gap-y-3 p-4 text-[14px]">
              <div>
                <dt className="text-[12.5px] text-ink-500">Category</dt>
                <dd className="text-ink-900">
                  {detail.category?.name ?? '—'}
                  {/* The seller's own label when nothing in the curated tree fitted.
                      If the same one keeps appearing, promote it to a real category. */}
                  {customCategory && (
                    <span className="badge badge-warning ml-1.5" title="Seller-entered category — not in the catalog tree">
                      Seller: {customCategory}
                    </span>
                  )}
                </dd>
              </div>
              {/* Products often have no formal brand; fall back to the seller's
                  business name (the uploader) so the field is never blank. */}
              <div>
                <dt className="text-[12.5px] text-ink-500">Brand</dt>
                <dd className="text-ink-900">{detail.brand?.name ?? detail.seller?.businessName ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[12.5px] text-ink-500">MRP</dt>
                <dd className="font-tabular font-semibold text-ink-900">₹{(detail.mrp ?? 0).toLocaleString('en-IN')}</dd>
              </div>
              <div>
                <dt className="text-[12.5px] text-ink-500">Selling price</dt>
                <dd className="font-tabular font-semibold text-ink-900">₹{(detail.sellingPrice ?? 0).toLocaleString('en-IN')}</dd>
              </div>
            </dl>

            {occasions.length > 0 && (
              <div className="mb-5 flex flex-wrap items-center gap-1.5">
                <span className="text-[13.5px] text-ink-500">Occasions:</span>
                {occasions.map((o) => (
                  <span key={o} className="badge badge-accent">{o}</span>
                ))}
              </div>
            )}

            <h3 className="mb-2 text-[14.5px] font-semibold text-ink-900">Variants ({detail.variants.length})</h3>
            <div className="overflow-hidden rounded-md border border-surface-line">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr><th>SKU</th><th className="!text-center">Colour</th><th className="!text-center">Size</th><th className="!text-right">MRP</th><th className="!text-right">Price</th><th className="!text-center">Active</th></tr>
                  </thead>
                  <tbody>
                    {detail.variants.map((v) => (
                      <tr key={v.id}>
                        <td className="sku text-ink-700">{v.sku}</td>
                        <td className="text-center">{v.color ?? '—'}</td>
                        {/* `size` already carries its unit in the label ("34 cm");
                            sizeUnit says which system the seller measured in. */}
                        <td className="whitespace-nowrap text-center">
                          {v.size ?? '—'}
                          {v.size && v.sizeUnit && v.sizeUnit !== 'ALPHA' && (
                            <span className="ml-1 text-[12.5px] text-ink-500">{v.sizeUnit}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right font-tabular">{v.mrpOverride != null ? `₹${v.mrpOverride.toLocaleString('en-IN')}` : '—'}</td>
                        <td className="whitespace-nowrap text-right font-tabular font-semibold">{v.sellingPriceOverride != null ? `₹${v.sellingPriceOverride.toLocaleString('en-IN')}` : '—'}</td>
                        <td className="text-center">
                          {v.isActive
                            ? <Symbol name="check" size={18} className="text-success-ink" label="Active" />
                            : <span className="text-ink-500">—</span>}
                        </td>
                      </tr>
                    ))}
                    {detail.variants.length === 0 && <tr><td colSpan={6} className="text-center text-ink-500">No variants yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        </div>

        {detail && canModerate && detail.status === 'PENDING_REVIEW' && (
          <div className="flex shrink-0 justify-end gap-2.5 border-t border-surface-line px-6 py-3.5">
            <button type="button" disabled={busy} onClick={() => onAction('reject')} className="btn btn-danger-outline">Reject</button>
            <button type="button" disabled={busy} onClick={() => onAction('approve')} className="btn btn-primary">Approve</button>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/85 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute right-5 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
          >
            <Symbol name="close" size={24} />
          </button>
          <img
            src={mediaSrc(lightbox)}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] rounded-md object-contain shadow-elev-4"
          />
        </div>
      )}
    </div>
  );
}

// Mirrors the API's lifecycle rules (catalog/product-moderation.ts): approve and
// reject only from the review queue, pause only live products, resume only
// paused ones. A rejected product comes back to the queue when the seller
// edits and resubmits it — it is never approved directly.
function RowActions({ status, busy, onAction }: { status: string; busy: boolean; onAction: (a: 'approve' | 'reject' | 'pause' | 'resume') => void }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      {status === 'PENDING_REVIEW' && (
        <button type="button" disabled={busy} onClick={() => onAction('approve')} className="btn btn-success btn-sm">Approve</button>
      )}
      {status === 'PENDING_REVIEW' && (
        <button type="button" disabled={busy} onClick={() => onAction('reject')} className="btn btn-danger-outline btn-sm">Reject</button>
      )}
      {status === 'ACTIVE' && (
        <button type="button" disabled={busy} onClick={() => onAction('pause')} className="btn btn-secondary btn-sm">Pause</button>
      )}
      {status === 'INACTIVE' && (
        <button type="button" disabled={busy} onClick={() => onAction('resume')} className="btn btn-tonal btn-sm">Resume</button>
      )}
      {status === 'DRAFT' && <span className="px-2 text-[12.5px] text-ink-500">Awaiting submit</span>}
      {status === 'REJECTED' && <span className="px-2 text-[12.5px] text-ink-500">Awaiting seller fix</span>}
    </div>
  );
}

/**
 * Product thumbnail that degrades to a neutral placeholder when the image is
 * missing or fails to load, instead of the browser's broken-image icon.
 */
function ProductThumb({ url, alt, className }: { url: string; alt: string; className: string }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div className={`${className} grid place-items-center bg-surface-2 text-ink-400`}>
        <ImageOff size={14} />
      </div>
    );
  }
  return (
    <img
      src={mediaSrc(url)}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} object-cover bg-surface-2`}
    />
  );
}

function SellerCell({ seller }: { seller: NonNullable<ProductRow['seller']> }) {
  const kyc = seller.kycStatus ?? seller.onboardingStatus;
  return (
    <div className="leading-tight">
      <div className="font-semibold text-ink-900">{seller.businessName}</div>
      {seller.ownerName && <div className="mt-0.5 text-[13px] text-ink-700">{seller.ownerName}</div>}
      {seller.phoneE164 && <div className="text-[13px] text-ink-500">{seller.phoneE164}</div>}
      {seller.email && <div className="text-[13px] text-ink-500">{seller.email}</div>}
      {kyc && (
        <span className={`badge mt-1.5 ${
          kyc === 'APPROVED' ? 'badge-success'
            : kyc === 'REJECTED' ? 'badge-danger'
              : ''}`}>
          KYC: {sentence(kyc)}
        </span>
      )}
    </div>
  );
}

/** Display-only sentence case for API enums: `PENDING_REVIEW` → "Pending review". */
function sentence(value: string): string {
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function StatusPill({ value }: { value: string }) {
  const tone =
    value === 'ACTIVE' || value === 'APPROVED' ? 'badge-success'
      : value === 'REJECTED' ? 'badge-danger'
        : value === 'PENDING_REVIEW' ? 'badge-accent'
          : '';
  return <span className={`badge ${tone}`}>{sentence(value)}</span>;
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div role="alert" className="alert alert-danger items-center justify-between">
      <span>{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="btn btn-quiet btn-icon btn-sm -my-1 text-sindoor-700">
          <Symbol name="close" size={18} />
        </button>
      )}
    </div>
  );
}
